/**
 * History Manager for temporal memory and undo/redo
 * 
 * Provides rewindable state snapshots for undo/redo functionality.
 * Records deep copies of state on each mutation (throttled).
 * Emits restoration events when undo/redo occurs.
 */

import { bus } from './events.js';
import { deepClone } from '../utils/clone.js';

/**
 * A state snapshot with metadata for history tracking.
 */
export interface Snapshot {
  id: string;
  timestamp: number;
  state: Record<string, unknown>;
  label?: string; // Optional user/system label (e.g., "Applied layout: Kanban")
}

/**
 * History Manager for temporal memory and undo/redo.
 * 
 * Stores deep copies of state snapshots and provides undo/redo functionality.
 * Emits events when history changes so UI can update accordingly.
 */
export class HistoryManager {
  private history: Snapshot[] = [];
  private pointer = -1;
  private enabled = true;

  /**
   * Record a state snapshot.
   * Truncates future history if user has branched (undo then new action).
   */
  record(state: Record<string, unknown>, label?: string): void {
    if (!this.enabled) return;

    const snap: Snapshot = {
      id: crypto.randomUUID(),
      timestamp: Date.now(),
      state: deepClone(state),
      label,
    };

    // Truncate future if user branched
    this.history = this.history.slice(0, this.pointer + 1);
    this.history.push(snap);
    this.pointer = this.history.length - 1;

    bus.emit('history.recorded', {
      id: snap.id,
      pointer: this.pointer,
      length: this.history.length,
    });
  }

  /**
   * Undo: move pointer back and restore previous state.
   */
  undo(): void {
    if (this.pointer <= 0) return;

    this.pointer--;
    const snap = this.history[this.pointer];
    bus.emit('state.restore', deepClone(snap.state));
    bus.emit('history.pointer', {
      pointer: this.pointer,
      length: this.history.length,
    });
  }

  /**
   * Redo: move pointer forward and restore next state.
   */
  redo(): void {
    if (this.pointer >= this.history.length - 1) return;

    this.pointer++;
    const snap = this.history[this.pointer];
    bus.emit('state.restore', deepClone(snap.state));
    bus.emit('history.pointer', {
      pointer: this.pointer,
      length: this.history.length,
    });
  }

  /**
   * Get the current snapshot.
   */
  current(): Snapshot | null {
    return this.pointer >= 0 ? this.history[this.pointer] : null;
  }

  /**
   * Clear all history.
   */
  clear(): void {
    this.history = [];
    this.pointer = -1;
    bus.emit('history.cleared', {});
  }

  /**
   * Enable or disable history recording.
   * Useful when restoring state to avoid recursive history pollution.
   */
  setEnabled(v: boolean): void {
    this.enabled = v;
  }

  /**
   * Get the current history pointer.
   */
  getPointer(): number {
    return this.pointer;
  }

  /**
   * Get the total history length.
   */
  getLength(): number {
    return this.history.length;
  }

  /**
   * Check if undo is possible.
   */
  canUndo(): boolean {
    return this.pointer > 0;
  }

  /**
   * Check if redo is possible.
   */
  canRedo(): boolean {
    return this.pointer < this.history.length - 1;
  }
}

/**
 * Global HistoryManager instance.
 */
export const history = new HistoryManager();

